import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { getBroadcast, setBroadcast } from "@/features/admin/api";
import { SEVERITY_BANNER_CLASS } from "@/features/admin/lib/severity";
import type { BroadcastSeverity } from "@/features/admin/types";
import { cn } from "@/lib/utils";

export default function BroadcastPage() {
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState<BroadcastSeverity>("danger");
  const [isActive, setIsActive] = useState(false);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    try {
      const b = await getBroadcast();
      setMessage(b.message);
      setSeverity(b.severity);
      setIsActive(b.isActive);
    } catch {
      toast.error("Couldn't load the broadcast.");
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function save(msg: string) {
    setBusy(true);
    try {
      const b = await setBroadcast(msg, severity);
      setIsActive(b.isActive);
      setMessage(b.message);
      toast.success(b.isActive ? "Broadcast is live." : "Broadcast cleared.");
    } catch {
      toast.error("Couldn't update the broadcast.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid max-w-2xl gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Broadcast banner</CardTitle>
          <CardDescription>
            A live banner shown to every logged-in user (delivered by short-poll). An empty
            message means no active broadcast.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          {isActive && (
            <Alert className={cn(SEVERITY_BANNER_CLASS[severity])}>
              <AlertTitle>Currently live</AlertTitle>
              <AlertDescription>{message}</AlertDescription>
            </Alert>
          )}
          <div className="grid gap-2">
            <Label htmlFor="broadcast-message">Message</Label>
            <Textarea
              id="broadcast-message"
              rows={3}
              maxLength={500}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="e.g. Scheduled maintenance tonight 10pm–11pm ET."
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="broadcast-severity">Severity</Label>
            <Select value={severity} onValueChange={(v) => setSeverity(v as BroadcastSeverity)}>
              <SelectTrigger id="broadcast-severity" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="danger">Danger (red)</SelectItem>
                <SelectItem value="warning">Warning (yellow)</SelectItem>
                <SelectItem value="success">Success (green)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button disabled={busy || !message.trim()} onClick={() => void save(message)}>
              Broadcast
            </Button>
            <Button variant="outline" disabled={busy || !isActive} onClick={() => void save("")}>
              Remove Broadcast
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
